import { useMemo } from "react";
import { MetricCardsContainer } from "@/components/metrics/metric-cards-container";
import type { MetricCardConfig } from "@/components/metrics/types";
import type { WebsitePageMetrics } from "@/hooks/api/use-website-page-metrics";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { useWebpageFilterState } from "@/hooks/use-webpage-filter-state";
import {
  DEFAULT_WEBPAGE_METRIC_ORDER,
  HIDDEN_WEBPAGE_METRICS,
  WEBPAGE_METRIC_CONFIG,
} from "./webpage-metrics-config";

interface WebpageMetricCardsProps {
  metrics: Partial<WebsitePageMetrics> | undefined;
  onMetricSelect: (metric: keyof WebsitePageMetrics) => void;
  isLoading?: boolean;
  loadingStates?: Record<
    keyof WebsitePageMetrics,
    { isLoading: boolean; isError: boolean; error: unknown }
  >;
  url?: string;
  leftControls?: React.ReactNode;
  showBody?: boolean;
  emptyBody?: React.ReactNode;
}

export function WebpageMetricCards({
  metrics,
  onMetricSelect,
  isLoading,
  loadingStates: propLoadingStates,
  leftControls,
  showBody,
  emptyBody,
}: WebpageMetricCardsProps) {
  const filterState = useWebpageFilterState("webpages::explorer");
  const {
    displayFilters,
    filterColumns,
    getTotalFilterCount,
    handleClearFilter,
    clearAllFilters,
  } = useMetricCardFilters(filterState);

  // Convert WEBPAGE_METRIC_CONFIG to the format expected by MetricCardsContainer
  const convertedMetricConfig = useMemo(() => {
    const config: Record<string, MetricCardConfig> = {};

    Object.entries(WEBPAGE_METRIC_CONFIG).forEach(([key, value]) => {
      config[key] = {
        title: value.title,
        tooltip: value.tooltip,
        category: value.category,
        render: (val: number | null, options: Record<string, unknown>) => {
          // For webpage metrics, the render function expects the value to be possibly undefined
          // and the original render function expects different parameters
          const metricKey = options.metricKey as keyof WebsitePageMetrics;
          const metrics = options.metrics as
            | Partial<WebsitePageMetrics>
            | undefined;
          return value.render(val ?? undefined, { metricKey, metrics });
        },
      };
    });

    return config;
  }, []);

  // Create loading states for all metrics
  const loadingStates = useMemo(() => {
    // Use prop loading states if provided, otherwise fall back to isLoading prop
    if (propLoadingStates) {
      return propLoadingStates as Record<
        string,
        { isLoading: boolean; isError: boolean }
      >;
    }

    const states: Record<string, { isLoading: boolean; isError: boolean }> = {};

    Object.keys(WEBPAGE_METRIC_CONFIG).forEach((key) => {
      states[key] = {
        isLoading: isLoading ?? false,
        isError: false,
      };
    });

    return states;
  }, [isLoading, propLoadingStates]);

  // Default renderMetric that uses the config's render functions
  const renderMetric = (
    value: number | null,
    options: Record<string, unknown>,
  ) => {
    const metricKey = options.metricKey as keyof WebsitePageMetrics;
    const config = WEBPAGE_METRIC_CONFIG[metricKey];

    if (config?.render) {
      return config.render(value ?? undefined, {
        metricKey,
        metrics: options.metrics as Partial<WebsitePageMetrics> | undefined,
      });
    }

    // Fallback - this shouldn't be reached if all configs have render functions
    return value?.toLocaleString() ?? "—";
  };

  return (
    <MetricCardsContainer<Partial<WebsitePageMetrics>>
      metrics={metrics}
      onMetricSelect={(metric) =>
        onMetricSelect(metric as keyof WebsitePageMetrics)
      }
      loadingStates={loadingStates}
      metricConfig={convertedMetricConfig}
      defaultOrder={DEFAULT_WEBPAGE_METRIC_ORDER as string[]}
      hiddenMetrics={HIDDEN_WEBPAGE_METRICS as string[]}
      wipMetrics={[]} // No WIP metrics for webpages
      storageKey="webpages::explorer::metrics::order"
      title="Top-level webpage metrics"
      entityScope="webpages::explorer"
      entity="webpage"
      renderMetric={renderMetric}
      getTotalFilterCount={getTotalFilterCount}
      onResetMetricFilters={clearAllFilters}
      displayFilters={displayFilters}
      filterColumns={filterColumns}
      clearFilter={handleClearFilter}
      leftControls={leftControls}
      showBody={showBody}
      emptyBody={emptyBody}
    />
  );
}
